import { useUserContext } from '@/contexts/UserContext';
import { getDynamicPageVariantsQuery } from '@/services/DynamicPageVariantsService';
import { dynamicPagesQuery } from '@/services/DynamicPagesService';
import { DynamicPage, DynamicPageVariant } from '@superfunnel/types';
import { getDocs, QueryDocumentSnapshot } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { DynamicPageWithVariants } from './types';

export interface UseVariantOriginDataReturn {
  dynamicPagesWithVariants: DynamicPageWithVariants[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch all dynamic pages with their variants for the VariantOriginModal
 */
export function useVariantOriginData(): UseVariantOriginDataReturn {
  const { selectedAccount } = useUserContext();
  const [dynamicPagesWithVariants, setDynamicPagesWithVariants] = useState<
    DynamicPageWithVariants[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!selectedAccount) {
      setDynamicPagesWithVariants([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch all dynamic pages
      const dynamicPagesSnapshot = await getDocs(
        dynamicPagesQuery(selectedAccount.id),
      );

      // Fetch variants for each dynamic page in parallel
      const results = await Promise.all(
        dynamicPagesSnapshot.docs.map(async (dpDoc) => {
          try {
            const variantsSnapshot = await getDocs(
              getDynamicPageVariantsQuery(selectedAccount.id, dpDoc.id),
            );
            return {
              dynamicPage: dpDoc as QueryDocumentSnapshot<DynamicPage>,
              variants:
                variantsSnapshot.docs as QueryDocumentSnapshot<DynamicPageVariant>[],
            };
          } catch (err) {
            console.error(
              `Failed to fetch variants for dynamic page ${dpDoc.id}:`,
              err,
            );
            return {
              dynamicPage: dpDoc as QueryDocumentSnapshot<DynamicPage>,
              variants: [],
            };
          }
        }),
      );

      setDynamicPagesWithVariants(results);
    } catch (err) {
      console.error('Failed to fetch dynamic pages:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to fetch dynamic pages'),
      );
    } finally {
      setIsLoading(false);
    }
  }, [selectedAccount]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    dynamicPagesWithVariants,
    isLoading,
    error,
    refetch: fetchData,
  };
}
